import { Box, Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useUser } from 'app/network/user';
import { Placeholder, PlaceholderLine, PlaceholderMedia, ShineOverlay } from 'app/overrides/rn-placeholder';
import { Pressable, View } from 'react-native';
import { ListingRequestCard } from './listing-request-card';
import { type Dispatch, type SetStateAction } from 'react';
import { GeoPin } from 'app/design/icons/geo-pin';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { CorpoImage } from './corp-image';
import { TutorialRef } from 'app/types/tutorial';
import type { Listing } from 'app/types/listing';
import { ListingType } from 'app/types/listing';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import { type ViewProps } from 'react-native-svg/lib/typescript/fabric/utils';
import { FavouriteButton, useFavouriteButton } from './favourite-button';
import { ImageBackground } from 'expo-image';
import { useRoute } from 'app/hooks/navigation';
import { SharerType } from 'app/types/preferences';
import { Checkbox } from 'app/design/primitives/checkbox';
import { type PopularItem } from 'app/types/category';
import { i18n } from 'app/localization/i18n';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

export const OFFER_CARD_HEIGHT = 100;
export const OFFER_CARD_WIDTH = 120;

/** Shared footer insets for onboarding / aggregate interest cards (popular items + listings). */
const INTEREST_CARD_FOOTER_STYLE = {
  paddingHorizontal: theme.spacing['spacing.general.x4'],
  paddingTop: theme.spacing['spacing.general.x2'],
  paddingBottom: theme.spacing['spacing.general.x4'],
} as const;

const INTEREST_CARD_TITLE_TEXT_STYLE = {
  flex: 1,
  minWidth: 0,
  lineHeight: 22,
  minHeight: 44,
} as const;

function PopularItemCardInterests({
  popularItem,
  selectedPopularItems,
  setSelectedPopularItems,
  fixedCardWidth,
}: {
  popularItem: PopularItem;
  selectedPopularItems: PopularItem[];
  setSelectedPopularItems?: Dispatch<SetStateAction<PopularItem[]>>;
  fixedCardWidth?: number;
}) {
  const selectedPopularItemIds = selectedPopularItems.map((item) => item.id);

  const onPressSelect = () => {
    setSelectedPopularItems?.((prevSelected: PopularItem[]) => {
      if (prevSelected.some((item) => item.id === popularItem.id)) {
        return prevSelected.filter((item) => item.id !== popularItem.id);
      } else {
        return [...prevSelected, popularItem];
      }
    });
  };

  const isSelected = selectedPopularItemIds.includes(popularItem.id);

  const popularItemName = popularItem[i18n.language as 'en'];

  const carouselSizing =
    fixedCardWidth !== undefined
      ? { width: fixedCardWidth, maxWidth: fixedCardWidth, flexGrow: 0, flexShrink: 0 }
      : { flex: 1, maxWidth: '50%' as const };

  /** Horizontal carousel rows: intrinsic height avoids stretched/clipped footers. Grid cells keep equal height. */
  const cardHeightStyle =
    fixedCardWidth !== undefined ? { alignSelf: 'flex-start' as const } : { height: '100%' as const };

  return (
    <Pressable
      onPress={onPressSelect}
      style={{
        borderRadius: theme.borderRadii['radius.xl'],
        backgroundColor: 'white',
        ...carouselSizing,
        ...cardHeightStyle,
        ...(isSelected
          ? {
              shadowOpacity: 1,
              shadowRadius: 5,
              shadowOffset: { width: 0, height: 0 },
              shadowColor: theme.colors['surface.brand.default'],
            }
          : {}),
      }}
    >
      <Box
        alignItems="center"
        aspectRatio={1}
        borderTopLeftRadius="radius.xl"
        borderTopRightRadius="radius.xl"
        overflow="hidden"
        width="100%"
      >
        <ImageBackground
          contentFit="contain"
          source={popularItem.pictureUrl}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
      <Column gap="spacing.general.x1" style={INTEREST_CARD_FOOTER_STYLE}>
        <Row alignItems="center" gap="spacing.general.x2" justifyContent="space-between">
          <Text fontWeight="700" numberOfLines={2} variant="bodyLarge" style={INTEREST_CARD_TITLE_TEXT_STYLE}>
            {popularItemName}
          </Text>
          <View
            style={{
              width: 24,
              height: 24,
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Checkbox onPress={onPressSelect} borderRadius="radius.lg" checked={isSelected} color="text.primary" />
          </View>
        </Row>
      </Column>
    </Pressable>
  );
}

function ListingCardInterests({
  listing,
  fixedCardWidth,
}: {
  fixedCardWidth?: number;
  listing: Listing & {
    hideCorpoBadge?: boolean;
    showUser?: boolean;
    isFirst?: boolean;
    isSelecting?: boolean;
    selectedRowsState?: [Listing['id'][], Dispatch<SetStateAction<Listing['id'][]>>];
    hideListingsSubtype?: boolean;
  };
}) {
  const { formatDistanceWithoutText } = useDistanceUnit();
  const { data: myUser } = useUser();
  const route = useRoute<'interests'>();
  const sharerType = route.params.sharerType;

  const {
    item,
    type,
    user,
    pictures = [],
    groups,
    hideCorpoBadge = false,
    showUser = true,
    distance,
    isFirst,
  } = listing;

  const { onPress: onPressFavourite, isFavourite } = useFavouriteButton(listing);

  const isAuthor = myUser?.id === user.id;

  const { viewRef: tutorialViewRef } = useTutorialRef(
    isFirst && type === ListingType.offer ? TutorialRef.FirstOfferInList : undefined,
  );

  if (type === ListingType.request) {
    return <ListingRequestCard listing={listing} isFirst={isFirst} showUser={showUser} />;
  }

  const carouselSizing =
    fixedCardWidth !== undefined
      ? { width: fixedCardWidth, maxWidth: fixedCardWidth, flexGrow: 0, flexShrink: 0 }
      : { flex: 1, maxWidth: '50%' as const };

  const cardHeightStyle =
    fixedCardWidth !== undefined ? { alignSelf: 'flex-start' as const } : { height: '100%' as const };

  return (
    <Pressable
      onPress={onPressFavourite}
      ref={tutorialViewRef}
      style={{
        borderRadius: theme.borderRadii['radius.xl'],
        backgroundColor: 'white',
        ...carouselSizing,
        ...cardHeightStyle,
        ...(isFavourite
          ? {
              shadowOpacity: 1,
              shadowRadius: 5,
              shadowOffset: { width: 0, height: 0 },
              shadowColor: theme.colors['surface.brand.default'],
            }
          : {}),
      }}
    >
      <Box
        alignItems="center"
        borderTopLeftRadius="radius.xl"
        borderTopRightRadius="radius.xl"
        overflow="hidden"
        width="100%"
      >
        <ImageBackground source={pictures[0]?.url} style={{ width: '100%', height: 115 }} />
        {groups?.length && !hideCorpoBadge ? (
          <CorpoImage picture={groups[0]?.picture} style={{ position: 'absolute', top: 6, left: 6 }} />
        ) : undefined}
      </Box>
      <Column gap="spacing.general.x1" style={INTEREST_CARD_FOOTER_STYLE}>
        <Row alignItems="center" gap="spacing.general.x2" justifyContent="space-between">
          <Text fontWeight="700" variant="bodyBase" numberOfLines={2} style={INTEREST_CARD_TITLE_TEXT_STYLE}>
            {item.name}
          </Text>
          {!isAuthor && sharerType === SharerType.borrower ? (
            <View style={{ flexShrink: 0 }}>
              <FavouriteButton listing={listing} />
            </View>
          ) : null}
        </Row>

        {sharerType === SharerType.borrower ? (
          <Row alignItems="center" gap="spacing.general.x2">
            {distance !== undefined ? (
              <Row alignItems="center">
                <GeoPin color={theme.colors['icon.secondary']} width={18} />
                <Text paddingLeft="spacing.general.x0,5">{formatDistanceWithoutText(distance)}</Text>
              </Row>
            ) : null}
          </Row>
        ) : null}
      </Column>
    </Pressable>
  );
}

function ListingCardSkeleton() {
  const leftView = (props: ViewProps) => (
    <PlaceholderMedia
      style={[
        {
          height: OFFER_CARD_HEIGHT,
          width: OFFER_CARD_WIDTH,
          borderRadius: theme.borderRadii['radius.md'],
        },
        props.style,
      ]}
    />
  );
  return (
    <Placeholder Animation={ShineOverlay} Left={leftView}>
      <PlaceholderLine style={{ width: 140, marginBottom: 20 }} />
      <PlaceholderLine style={{ width: 50, marginBottom: 40 }} />

      <PlaceholderLine style={{ width: 120, height: 30, borderRadius: theme.borderRadii['radius.md'] }} />
    </Placeholder>
  );
}

export { ListingCardInterests, ListingCardSkeleton, PopularItemCardInterests };
